<template>
  <!-- KangarooCareRoadmapVertical.vue -->
  <main class="kc-vertical">
    <!-- Header -->
    <header class="kc-header">
      <div class="kc-title">早产儿追赶性生长标准化时间轴计划</div>
      <div class="kc-sub">跟着计划走，宝宝稳步追赶，妈妈更省心。</div>
    </header>

    <!-- Loading状态 -->
    <div v-if="loading" class="loading">
      <p>正在加载护理计划...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="fetchPlanData" class="retry-btn">重新加载</button>
    </div>

    <!-- Vertical timeline -->
    <section v-else class="kc-timeline" ref="timelineEl">
      <article
        v-for="(p, i) in phasesComputed"
        :key="p.id"
        :id="p.id"
        class="kc-item"
        :class="{ 'is-active': activeId === p.id, 'is-done': i > activeIndex }"
      >
        <!-- Left rail: dot + line -->
        <div class="kc-rail" aria-hidden="true">
          <span class="kc-dot"></span>
          <span class="kc-line"></span>
        </div>

        <!-- Right card -->
        <div class="kc-card">
          <header class="kc-card-head">
            <div class="kc-badge" :class="`c${(i % 5) + 1}`">{{ p.badge }}</div>
            <!-- <div class="kc-head-meta">
              <span class="kc-status" :data-status="statusLabel(i)">{{ statusLabel(i) }}</span>
              <span v-if="p.eta" class="kc-eta">预计: {{ p.eta }}</span>
            </div> -->
          </header>

          <div class="kc-summary">
            <div class="kc-summary-title">关键节点</div>
            <div class="kc-summary-box">{{ p.summary }}</div>
          </div>
          
          <!-- 新增：核心目标部分 -->
          <div class="kc-aims" v-if="p.aims && p.aims.length">
            <button class="kc-steps-toggle" @click="toggle('aims_' + i)">
              核心目标
              <span class="kc-chevron" :class="{ on: opened['aims_' + i] }">▾</span>
            </button>
            <ol class="kc-steps-list" v-show="opened['aims_' + i]">
              <li v-for="(aim, k) in p.aims" :key="k">{{ aim }}</li>
            </ol>
          </div>
          
          <!-- 原有的主要步骤部分 -->
          <div class="kc-steps" v-if="p.steps && p.steps.length">
            <button class="kc-steps-toggle" @click="toggle(i)">
              主要步骤
              <span class="kc-chevron" :class="{ on: opened[i] }">▾</span>
            </button>
            <ol class="kc-steps-list" v-show="opened[i]">
              <li v-for="(s, j) in p.steps" :key="j">{{ s }}</li>
            </ol>
          </div>
        </div>
      </article>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import { TOKEN } from '@/utils/config';
export default {
    name: "KangarooCareRoadmapVertical",
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        activeIndexInit: {
            type: Number,
            default: 0,
        },
    },
    data () {
        return {
            apiData: [], // 从API获取的数据
            loading: false, // 加载状态
            error: null, // 错误信息
            opened: {},
            timelineEl: null,
            activeIndex: 0,
            io: null,
        };
    },
    computed: {
        phasesComputed () {
            // 如果传入了 modelValue，优先使用；否则使用API数据
            if (this.modelValue?.length) {
                return this.modelValue;
            }
            return this.transformApiData(this.apiData);
        },
        activeId () {
            return this.phasesComputed[this.activeIndex]?.id;
        },
    },
    mounted () {
        let type = this.$route.query.type;
        if (type == 'test') {
            this.token = TOKEN;
        } else {
            let token = this.$route.query.token;
            this.token = token;
        }

        this.activeIndex = this.activeIndexInit;
        this.opened[this.activeIndex] = true;
        
        // 如果没有传入modelValue，则从API获取数据
        if (!this.modelValue?.length) {
            this.fetchPlanData();
        } else {
            this.setupIntersectionObserver();
        }
    },
    methods: {
        // 获取API数据
        async fetchPlanData () {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get('https://api.yubinglabs.com/aiplan/plan', {
                    headers: {
                        Authorization: 'bearer ' + this.token,
                        channel: 1000
                    }
                });
                
                if (response.data.success && response.data.result) {
                    this.apiData = response.data.result;
                    
                    // 初始化所有项目为展开状态（包括核心目标和主要步骤）
                    this.apiData.forEach((item, index) => {
                        this.$set(this.opened, index, true); // 主要步骤
                        this.$set(this.opened, 'aims_' + index, true); // 核心目标
                    });
                    
                    this.$nextTick(() => {
                        this.setupIntersectionObserver();
                    });
                } else {
                    this.error = '获取护理计划失败';
                }
            } catch (err) {
                console.error('获取护理计划失败:', err);
                this.error = '网络错误，请检查网络连接';
            } finally {
                this.loading = false;
            }
        },

        // 将API返回的数据转换为组件需要的格式
        transformApiData (apiData) {
            if (!Array.isArray(apiData) || apiData.length === 0) {
                return [];
            }
        
            return apiData.map(item => {
                // 将action_content按换行符分割成steps数组
                const steps = item.action_content 
                    ? item.action_content.split('\n').filter(step => step.trim()) 
                    : [];
        
                // 将main_aim按换行符分割成aims数组
                const aims = item.main_aim 
                    ? item.main_aim.split('\n').filter(aim => aim.trim()) 
                    : [];
        
                return {
                    id: item.id,
                    badge: item.time_node || '无描述',
                    summary: item.action_type || '未知阶段',
                    aims: aims, // 新增核心目标数组
                    steps: steps,
                    sort: item.sort,
                };
            });
        },

        toggle (i) {
            this.$set(this.opened, i, !this.opened[i]);
        },
        
        statusLabel (i) {
            if (i < this.activeIndex) {return "已完成";}
            if (i === this.activeIndex) {return "进行中";}
            return "待执行";
        },
        
        nextId (i) {
            const n = this.phasesComputed[i + 1];
            return n?.id || null;
        },
        
        buildThresholdList () {
            const thresholds = [];
            const numSteps = 20;
            for (let i = 0; i <= numSteps; i++) {
                thresholds.push(i / numSteps);
            }
            return thresholds;
        },
        
        setupIntersectionObserver () {
            // 先断开之前的观察器
            if (this.io) {
                this.io.disconnect();
            }

            this.io = new IntersectionObserver(
                (entries) => {
                    let best = { ratio: 0, id: null, idx: 0 };
                    entries.forEach((e) => {
                        if (e.isIntersecting && e.intersectionRatio > best.ratio) {
                            const id = e.target.id;
                            const idx = this.phasesComputed.findIndex((x) => x.id === id);
                            best = { ratio: e.intersectionRatio, id, idx };
                        }
                    });
                    if (best.id !== null) {
                        this.activeIndex = best.idx;
                    }
                },
                { root: null, rootMargin: "-20% 0px -55% 0px", threshold: this.buildThresholdList() }
            );

            this.phasesComputed.forEach((p) => {
                const el = document.getElementById(p.id);
                if (el) {
                    this.io.observe(el);
                }
            });
        },
    },
    
    watch: {
        modelValue: {
            deep: true,
            handler (val) {
                if (val?.length) {
                    this.$nextTick(() => {
                        this.setupIntersectionObserver();
                    });
                }
            },
        },
    },
    
    beforeDestroy () {
        this.io?.disconnect();
    },
};
</script>

<style scoped>
/* 原有样式保持不变 */
:root {
  --ink: #1f2937;   /* neutral-800 */
  --muted: #6b7280; /* neutral-500 */
  --line: #e5e7eb;  /* neutral-200/300 */
  --card: #fff;
  --accent: #fb923c; /* orange-400 */
}

.kc-vertical {
  max-width: 880px;
  margin: 0 auto;
  padding: 18px 14px 60px;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "PingFang SC", "Noto Sans CJK SC", "Microsoft YaHei", sans-serif;
  color: var(--ink);
}

/* 加载和错误状态样式 */
.loading, .error {
  text-align: center;
  padding: 40px 20px;
}

.loading p {
  font-size: 16px;
  color: var(--muted);
}

.error p {
  font-size: 16px;
  color: #ef4444;
  margin-bottom: 16px;
}

.retry-btn {
  background: #05C2CB;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  opacity: 0.9;
}

/* Header */
.kc-header {
  text-align: center;
  margin-bottom: 12px;
}
.kc-title {
  font-weight: 800;
  font-size: 22px;
}
.kc-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

/* Timeline list */
.kc-timeline {
  position: relative;
}
.kc-item {
  position: relative;
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 10px;
  padding: 10px 0;
  scroll-margin-top: 80px; /* for in-page anchor jumps */
}
.kc-item.is-active .kc-dot {
  transform: scale(1.15);
  box-shadow: 0 0 0 4px rgba(251, 146, 60, 0.2);
}
.kc-item.is-done .kc-dot {
  background: #05C2CB;
  border-color: #05C2CB;
}
.kc-rail {
  position: relative;
  display: grid;
  justify-items: center;
  margin-top: 16px;
}
.kc-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: var(--accent);
  border: 2px solid var(--accent);
  transition: transform 0.2s ease;
  z-index: 1;
}
.kc-line {
  position: absolute;
  top: 14px;
  bottom: -10px;
  width: 2px;
  background: var(--line);
  border-radius: 2px;
  background: #05C2CB;
}

/* Card */
.kc-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .04);
}
.kc-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  flex-wrap: wrap;
}
.kc-badge {
  padding: 6px 10px;
  border-radius: 8px;
  color: #fff;
  font-weight: 800;
  font-size: 12px;
}
.kc-badge.c1 { background: #0ea5e9; } /* sky */
.kc-badge.c2 { background: #10b981; } /* emerald */
.kc-badge.c3 { background: #f59e0b; } /* amber */
.kc-badge.c4 { background: #8b5cf6; } /* violet */
.kc-badge.c5 { background: #ef4444; } /* red */

.kc-head-meta {
  display: flex;
  gap: 10px;
  align-items: center;
}
.kc-status {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #f9fafb;
  color: var(--ink);
}
.kc-status[data-status="已完成"] {
  background: #ecfdf5;
  color: #065f46;
  border-color: #a7f3d0;
}
.kc-status[data-status="进行中"] {
  background: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}
.kc-eta {
  font-size: 11px;
  color: var(--muted);
}

/* Summary */
.kc-summary-title {
  margin-top: 10px;
  font-weight: 800;
  font-size: 13px;
}
.kc-summary-box {
  margin-top: 6px;
  border: 2px solid rgba(14, 165, 233, 0.5);
  background: #f0f9ff;
  padding: 10px;
  border-radius: 8px;
  line-height: 1.45;
  font-size: 13px;
}

/* Steps */
.kc-steps {
  margin-top: 10px;
}
.kc-steps-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--line);
  background: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
}
.kc-chevron {
  transition: transform .2s ease;
}
.kc-chevron.on { transform: rotate(180deg); }
.kc-steps-list {
  margin: 8px 0 0 18px;
}
.kc-steps-list li {
  margin: 4px 0;
  line-height: 1.45;
  font-size: 13px;
}

/* Actions */
.kc-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.kc-btn {
  appearance: none;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  padding: 8px 12px;
  font-size: 13px;
  border-radius: 10px;
}
.kc-btn.kc-primary {
  border-color: #fdba74;
  background: #ffedd5;
  color: #9a3412;
}

/* Mobile tweaks */
@media (max-width: 640px) {
  .kc-title { font-size: 20px; }
  .kc-summary-box, .kc-steps-list li { font-size: 12px; }
  .kc-card { padding: 12px; }
}

/* Aims - 核心目标 */
.kc-aims {
  margin-top: 10px;
}

/* 为了区分核心目标和主要步骤，可以给核心目标添加不同的样式 */
.kc-aims .kc-steps-toggle {
  background: #f0f9ff; /* 浅蓝色背景 */
  border-color: #0ea5e9;
  color: #0369a1;
}

.kc-aims .kc-steps-list {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  padding-left: 15px;
  margin-left: 3px;
}
</style>